import React from 'react';

import {
    faChevronDoubleLeft,
    faChevronLeft,
    faChevronRight,
    faChevronDoubleRight,
    faChevronDown as fasChevronDown,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type Table } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import { tableMessages } from 'src/registry/components/table/utils/messages';
import { pageSizes } from 'src/registry/hooks/usePagination';
import { Button } from 'src/registry/ui/Button';
import {
    DropdownMenu,
    DropdownMenuRadioItem,
    DropdownMenuRadioGroup,
    DropdownMenuContent,
    DropdownMenuTrigger,
} from 'src/registry/ui/DropdownMenu';
import { Input } from 'src/registry/ui/Input';
import { Skeleton } from 'src/registry/ui/Skeleton';

export type DataTablePaginationProps<TData> = {
    table: Table<TData>;
    isLoading?: boolean;
    isError?: boolean;
    count: number | undefined;
};

export function DataTablePagination<TData>({ table, isError, count }: DataTablePaginationProps<TData>) {
    const ref = React.useRef(null);
    const [page, setPage] = React.useState<string>((table.getState().pagination.pageIndex + 1).toString());

    const { formatMessage } = useIntl();

    // We manage the input as to not allow illegal values
    const onChangedPageInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const targetValue = parseInt(e.target.value);
        if (targetValue < 1) setPage('1');
        else if (targetValue > table.getPageCount()) setPage(table.getPageCount().toString());
        else setPage(e.target.value);
    };

    // Submit the page change only on blur
    const onBlurredPageInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const targetValue = parseInt(e.target.value);
        if (isNaN(targetValue) || !targetValue) setPage((table.getState().pagination.pageIndex + 1).toString());
        else table.setPageIndex(targetValue - 1);
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.code === 'Enter' && page) table.setPageIndex(Number(page) - 1);
    };

    React.useEffect(() => {
        setPage((table.getState().pagination.pageIndex + 1).toString());
    }, [table.getState().pagination.pageIndex]);

    if (isError) return <></>;

    return (
        <div className="flex flex-wrap-reverse items-center justify-end gap-6">
            <div className="flex items-center gap-2 text-sm text-gray-500">
                <span>{formatMessage(tableMessages.perPage)}</span>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="outline" className="text-gray-900" size="sm">
                            <FontAwesomeIcon icon={fasChevronDown} className="text-primary-700 size-3" />
                            <span>{table.getState().pagination.pageSize}</span>
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="">
                        <DropdownMenuRadioGroup
                            value={table.getState().pagination.pageSize.toString()}
                            onValueChange={(value) => {
                                table.setPageSize(parseInt(value));
                            }}
                        >
                            {Object.values(pageSizes).map((option, i) => (
                                <DropdownMenuRadioItem value={option.toString()} key={`page-size-option-${i}`}>
                                    {option}
                                </DropdownMenuRadioItem>
                            ))}
                        </DropdownMenuRadioGroup>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500">
                <span>{formatMessage(tableMessages.page)}</span>
                <Input
                    min={1}
                    max={table.getPageCount()}
                    type="number"
                    ref={ref}
                    value={page}
                    onKeyDown={onKeyDown}
                    onChange={onChangedPageInput}
                    onBlur={onBlurredPageInput}
                    className="h-9 w-16 px-1 font-mono text-gray-900"
                />
                <span>{formatMessage(tableMessages.of)}</span>
                {count == null ? (
                    <Skeleton className="h-6 w-4" />
                ) : (
                    <span className="font-mono text-gray-900">
                        {Math.max(Math.ceil(count / table.getState().pagination.pageSize), 1)}
                    </span>
                )}
            </div>
            <div className="flex items-center gap-2">
                <Button
                    variant="outline"
                    className="size-9"
                    onClick={() => table.setPageIndex(0)}
                    disabled={!table.getCanPreviousPage()}
                >
                    <FontAwesomeIcon icon={faChevronDoubleLeft} />
                </Button>
                <Button
                    variant="outline"
                    className="size-9"
                    onClick={() => table.previousPage()}
                    disabled={!table.getCanPreviousPage()}
                >
                    <FontAwesomeIcon icon={faChevronLeft} />
                </Button>
                <Button
                    variant="outline"
                    className="size-9"
                    onClick={() => table.nextPage()}
                    disabled={!table.getCanNextPage()}
                >
                    <FontAwesomeIcon icon={faChevronRight} />
                </Button>
                <Button
                    variant="outline"
                    className="size-9"
                    onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                    disabled={!table.getCanNextPage()}
                >
                    <FontAwesomeIcon icon={faChevronDoubleRight} />
                </Button>
            </div>
        </div>
    );
}
